import { useCallback, useMemo, useState } from 'react';
import {
  aiResponseModeSchema,
  buildAiResponseQualityPrompt,
  type AiResponseMode,
} from '@pirouette/shared';

export const AI_RESPONSE_MODE_STORAGE_KEY = 'pirouette:ai-response-mode:v1';

function readStoredMode(fallback: AiResponseMode): AiResponseMode {
  try {
    return aiResponseModeSchema.parse(window.localStorage.getItem(AI_RESPONSE_MODE_STORAGE_KEY));
  } catch {
    return fallback;
  }
}

export function useAiResponseMode(defaultMode: AiResponseMode = 'evidence') {
  const [mode, setModeState] = useState<AiResponseMode>(() => readStoredMode(defaultMode));

  const setMode = useCallback((nextMode: AiResponseMode) => {
    setModeState(nextMode);
    try {
      window.localStorage.setItem(AI_RESPONSE_MODE_STORAGE_KEY, nextMode);
    } catch {
      // Persistence is a convenience; the selected mode still applies in-memory.
    }
  }, []);

  const prompt = useMemo(() => buildAiResponseQualityPrompt(mode), [mode]);

  return { mode, setMode, prompt };
}

export type AiResponseModeController = ReturnType<typeof useAiResponseMode>;
